import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { X, MapPin } from "lucide-react";
import api from "@/lib/api";
import SectionHeading from "@/components/SectionHeading";
import { GALLERY } from "@/constants/site";
import { useSectionContent } from "@/hooks/useContent";

const SPANS = ["md:col-span-3 md:row-span-2", "md:col-span-3", "md:col-span-2", "md:col-span-4", "md:col-span-3", "md:col-span-3"];

export default function Gallery() {
  const [active, setActive] = useState(null);
  const c = useSectionContent("gallery");

  const { data: dbPhotos } = useQuery({
    queryKey: ["gallery-photos"],
    queryFn: async () => (await api.get("/gallery-photos")).data,
  });

  const photos =
    dbPhotos && dbPhotos.length > 0
      ? dbPhotos.map((p, i) => ({ ...p, url: p.image_url, span: SPANS[i % SPANS.length] }))
      : GALLERY;

  return (
    <section
      id="galleria"
      data-testid="gallery-section"
      className="py-24 sm:py-32 px-5 sm:px-8 bg-[#121215]"
    >
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          overline={c.overline}
          title={c.title}
          description={c.description}
          testid="gallery-heading"
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-6 auto-rows-[240px] gap-4">
          {photos.map((photo, i) => (
            <motion.button
              key={photo.url}
              type="button"
              data-testid={`gallery-photo-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              onClick={() => setActive(photo)}
              className={`group relative overflow-hidden leather-card p-0 text-left ${photo.span}`}
            >
              <img
                src={photo.url}
                alt={photo.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/95 via-[#0A0A0B]/20 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">
                  <MapPin className="w-3 h-3" /> Campania
                </p>
                <h3 className="mt-1 font-display text-lg sm:text-xl font-semibold text-[#F4EBE1]">
                  {photo.title}
                </h3>
                <p className="mt-1 text-xs text-[#C0B9B0] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  {photo.caption}
                </p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-[#0A0A0B]/95 backdrop-blur-sm flex items-center justify-center p-5"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="gallery-lightbox-close"
              className="absolute top-6 right-6 p-3 border border-[#C5A059]/40 text-[#F4EBE1] hover:border-[#C5A059]"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={active.url}
                alt={active.title}
                className="w-full max-h-[75vh] object-contain ring-1 ring-[#C5A059]/30"
              />
              <figcaption className="mt-4 text-center">
                <p className="font-display text-lg text-[#F4EBE1]">{active.title}</p>
                <p className="text-sm text-[#C0B9B0] mt-1">{active.caption}</p>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
